/**
 * `v-enter-advance` — Enter springt zum nächsten Eingabefeld.
 *
 * In HTML bewegt Enter den Fokus nicht; das ist Tab. Für ein Auditformular mit
 * dutzenden Zahlenfeldern hintereinander ist das Tippen ohne Handwechsel aber
 * spürbar schneller, deshalb diese Ergänzung.
 *
 * Bewusst eng gefasst, weil Enter anderswo bereits eine Bedeutung hat, die nicht
 * überschrieben werden darf:
 *
 * - **Auswahlfelder** (`v-select`, `v-autocomplete`, `v-combobox`) übernehmen mit
 *   Enter den markierten Eintrag. Sie tragen `role="combobox"` und sind
 *   ausgenommen.
 * - **Mehrzeilige Felder** brauchen Enter für den Zeilenumbruch.
 * - **Offene Overlays** (Dropdown, Datumsauswahl) besitzen Enter, solange sie
 *   offen sind.
 * - **Absenden per Enter** ist die Erwartung von Tastatur- und
 *   Screenreader-Nutzern. Deshalb wird nur *innerhalb* der Feldkette
 *   weitergesprungen; im letzten Feld gibt der Fokus frei, statt zu springen.
 *
 * Ausgelöst wird also nur in einzeiligen Text- und Zahlenfeldern.
 */
import type { ObjectDirective } from 'vue'

const FOCUSABLE = 'input:not([type="hidden"]), textarea, select'

/** Einzeiliges Text- oder Zahlenfeld — nur dort greift Enter. */
const TYPING_TYPES = ['text', 'number', 'tel', 'email', 'search', 'url', '']

function isTypingField(element: EventTarget | null): element is HTMLInputElement {
  if (!(element instanceof HTMLInputElement)) return false
  // v-select / v-autocomplete / v-combobox: Enter wählt dort aus.
  if (element.getAttribute('role') === 'combobox') return false
  if (element.readOnly || element.disabled) return false

  return TYPING_TYPES.includes(element.type)
}

/**
 * Bewusst über die berechnete Darstellung statt über `offsetParent`: letzteres
 * ist ohne Layout-Engine immer null, was die Direktive in Tests wirkungslos
 * machen würde. `v-show` setzt `display:none` am Element selbst, `v-if` entfernt
 * es ganz — beide Fälle sind damit abgedeckt.
 */
function isReachable(element: Element): boolean {
  const field = element as HTMLInputElement
  if (field.disabled) return false
  if (field.type === 'hidden') return false
  if ((field as HTMLElement).hidden) return false

  const style = window.getComputedStyle(field)
  return style.display !== 'none' && style.visibility !== 'hidden'
}

/** Zahlenfelder haben laut Spezifikation keine Textauswahl. */
const SELECTABLE_TYPES = ['text', 'search', 'url', 'tel', 'password', '']

function selectContent(field: HTMLInputElement) {
  if (!SELECTABLE_TYPES.includes(field.type)) return
  try {
    field.select()
  } catch {
    // Kein Grund, den Sprung scheitern zu lassen.
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  // Während einer IME-Eingabe schließt Enter die Komposition ab.
  if (event.isComposing) return
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return

  if (!isTypingField(event.target)) return

  // Ein offenes Overlay besitzt Enter, auch wenn der Fokus im Textfeld liegt.
  if (document.querySelector('.v-overlay--active')) return

  const container = event.currentTarget as HTMLElement
  const fields = Array.from(container.querySelectorAll(FOCUSABLE)).filter(isReachable)
  const index = fields.indexOf(event.target)
  if (index === -1) return

  // Erst hier: bis hierhin hätte Enter etwas anderes bedeuten können.
  event.preventDefault()

  const next = fields[index + 1] as HTMLInputElement | undefined
  if (!next) {
    // Letztes Feld: Eingabe abschließen, aber nicht ungefragt speichern.
    event.target.blur()
    return
  }

  next.focus()
  // Inhalt markieren, damit Weitertippen den alten Wert ersetzt — das ist beim
  // Durchlaufen vorbelegter Felder fast immer die Absicht.
  if (isTypingField(next)) {
    selectContent(next)
  }
}

export const enterAdvance: ObjectDirective<HTMLElement> = {
  mounted(el) {
    el.addEventListener('keydown', handleKeydown)
  },
  unmounted(el) {
    el.removeEventListener('keydown', handleKeydown)
  },
}

export default enterAdvance
